// ============= Imports =============
import { useContext } from 'react';
import { ToastContext } from '@/components/common/Toast/ToastContainer';

// ============= Types =============

/**
 * Toast function signatures used across SignalR relay hub handlers.
 *
 * This is intentionally defined here (in the hooks layer) so it can be shared
 * by any hook that needs to work with toast helpers without introducing
 * a hard dependency on specific handler modules.
 */
export interface ToastFunctions {
  showSuccess: (message: string, duration?: number) => void;
  showError: (message: string, duration?: number) => void;
  showInfo: (message: string, duration?: number) => void;
  showWarning: (message: string, duration?: number) => void;
}

// ============= Utilities =============

/**
 * Safe hook for accessing toast helpers.
 *
 * Unlike a strict `useToast` hook, this will gracefully return `undefined`
 * when the `ToastContext` provider is not present in the tree instead of
 * throwing (follows error-handling principle: never throw from hooks when
 * the feature is optional). Use in SignalR relay hub hooks where toasts may
 * be unavailable.
 *
 * @param enableToasts - When false, always returns `undefined` to effectively disable toasts.
 * @returns Toast helper functions, or `undefined` when not available or disabled.
 */
export const useToastSafe = (
  enableToasts: boolean = true
): ToastFunctions | undefined => {
  // Always call hooks unconditionally; gate the result on `enableToasts`.
  const context = useContext(ToastContext);

  if (!enableToasts) {
    return undefined;
  }

  return context || undefined;
};

